import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Checkbox,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EmptyProjectsIcon from "../assets/iconComponents/EmptyProjectsIcon";
import { useProject } from "../context/ProjectContext";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../context/NotificationContext";
import { useAllProjects } from "../context/AllProjectsContext";
import ProjectService from "../services/ProjectService";
import UsageService from "../services/UsageService";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import SearchBar from "../components/common/SearchBar";
import ProjectListItem from "../components/projects/ProjectListItem";
import SortingIcon from "../components/SortingIcon";
import PaginationComponent from "../components/PaginationComponent";
import CreateProjectModal from "../components/modals/CreateProjectModal";
import DeleteProjectsConfirmationModal from "../components/modals/DeleteProjectsConfirmationModal";
import YoutubeVideoDialog from "../components/common/YoutubeVideoDialog";
import { sortTableItems } from "../utils/sortTableItems";
import { PROJECTS_START_TUTORIAL_VIDEO_ID } from "../constants/tutorialVideos";

const getProjectsWelcomeDismissedStorageKey = (userId) =>
  `projectsWelcomeDismissed_${userId || "anonymous"}`;

const Projects = () => {
  const { selectedProject, setSelectedProject } = useProject();
  const { user, userId } = useAuth();
  const { setAllProjects } = useAllProjects();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showNotification = useNotification();
  const [projectsList, setProjectsList] = useState([]);
  const [checkedProjects, setCheckedProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [projectURL, setProjectURL] = useState("");
  const [projectsToggler, setProjectsToggler] = useState(false);
  const [projectsToDelete, setProjectsToDelete] = useState([]);
  const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "project",
    direction: "asc",
  });
  const [page, setPage] = useState(1);
  const [usageMap, setUsageMap] = useState({});
  const [isProjectsTutorialOpen, setIsProjectsTutorialOpen] = useState(false);
  const [isProjectsWelcomeDismissed, setIsProjectsWelcomeDismissed] =
    useState(false);
  const openAnchorEl = Boolean(anchorEl);
  const projectsPerPage = 10;

  useEffect(() => {
    const storageKey = getProjectsWelcomeDismissedStorageKey(userId);
    setIsProjectsWelcomeDismissed(
      localStorage.getItem(storageKey) === "true"
    );
  }, [userId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await ProjectService.getAllProjects(1, 1000);
        // Only auto-open modal if user has no projects AND does NOT have acme credentials (demo project credentials)
        if (!res.projects.length && page === 1) {
          const hasAcmeCredentials =
            !!user?.acme_user_email && !!user?.acme_user_password;
          if (!hasAcmeCredentials) {
            setOpen(true);
          }
        }
        const projects = res.projects || [];
        setProjectsList(projects);
        setAllProjects(projects);
        if (userId) {
          localStorage.setItem(`allProjects_${userId}`, JSON.stringify(projects));
        }
        if (
          selectedProject?.id &&
          !projects.some((project) => project.id === selectedProject.id)
        ) {
          sessionStorage.setItem("projectSelectionCleared", "true");
          setSelectedProject(undefined);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };

    fetchData();

    const fetchUsage = async () => {
      try {
        const res = await UsageService.getCurrentMonthUsageByUser();
        setUsageMap(res.usageMap || {});
      } catch (error) {
        console.error("Error fetching usage:", error);
      }
    };
    fetchUsage();
    // eslint-disable-next-line
  }, [projectsToggler, user, userId, selectedProject?.id, setAllProjects, setSelectedProject]);

  useEffect(() => {
    if (sessionStorage.getItem("projectAccessLost") === "true") {
      sessionStorage.removeItem("projectAccessLost");
      showNotification("You don’t have access to this project.", "warning");
    }
  }, [showNotification]);

  useEffect(() => {
    const projectId = searchParams.get("projectId");
    if (!projectId || !projectsList.length) return;

    const sharedProject = projectsList.find((project) => project.id === projectId);
    if (sharedProject) {
      setSelectedProject(sharedProject);
      navigate("/pages", { replace: true });
    }
  }, [projectsList, searchParams, setSelectedProject, navigate]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleSearch = (event, newValue) => {
    setSearchQuery(newValue?.toLowerCase() || "");
    setPage(1);
  };

  const handleOpenDeleteDialog = (projectID) => {
    if (projectID) {
      setProjectsToDelete([projectID]);
    } else {
      setProjectsToDelete(checkedProjects);
    }
    setDeleteOpen(true);
  };

  const handleOpenCreateDialog = () => {
    setOpen(true);
  };

  const handleClickCheckAllDropdown = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseCheckAllDropdown = () => {
    setAnchorEl(null);
  };

  const handleCheckAllCurrentPage = () => {
    const currentPageProjectIds = paginatedProjects
      .filter((project) => project?.is_owner && !project?.is_demo)
      .map((project) => project.id);
    setCheckedProjects((prevCheckedProjects) => [
      ...new Set([...prevCheckedProjects, ...currentPageProjectIds]),
    ]);
    setIsHeaderCheckboxChecked(true);
    handleCloseCheckAllDropdown();
  };

  const handleUncheckAllCurrentPage = () => {
    const currentPageProjectIds = paginatedProjects.map(
      (project) => project.id
    );
    setCheckedProjects((prevCheckedProjects) =>
      prevCheckedProjects.filter(
        (projectId) => !currentPageProjectIds.includes(projectId)
      )
    );
    setIsHeaderCheckboxChecked(false);
    handleCloseCheckAllDropdown();
  };

  const handleCheckboxChange = (id) => {
    setCheckedProjects((prevCheckedProjects) => {
      if (prevCheckedProjects.includes(id)) {
        return prevCheckedProjects.filter((projectId) => projectId !== id);
      } else {
        return [...prevCheckedProjects, id];
      }
    });
  };

  const handleOpenShareSettings = (projectId) => {
    navigate(`/project/${projectId}`, { state: { activeTab: "4" } });
  };

  const handleSortChange = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handleDismissProjectsWelcome = () => {
    const storageKey = getProjectsWelcomeDismissedStorageKey(userId);
    localStorage.setItem(storageKey, "true");
    setIsProjectsWelcomeDismissed(true);
  };

  const filteredProjects = projectsList.filter((project) =>
    project.name.toLowerCase().includes(searchQuery)
  );

  const sortedProjects = sortTableItems(filteredProjects, sortConfig);
  const paginatedProjects = sortedProjects.slice(
    (page - 1) * projectsPerPage,
    page * projectsPerPage
  );

  return (
    <Box className="rootWrapper">
      <Helmet>
        {selectedProject?.name ? (
          <title>{`Treegress - ${selectedProject.name} Projects`}</title>
        ) : (
          <title>Treegress - Projects</title>
        )}
      </Helmet>
      <Header projectsToggler={projectsToggler} />
      <Box className="srollContent">
        <Sidebar projectsListLength={projectsList.length} />
        <Box className="projects">
          {projectsList.length ? (
            <>
              <Box className="projectsTopSection">
                <Typography className="projectsTitle">Projects</Typography>
                <Box className="projectsActions">
                  {checkedProjects.length ? (
                    <Button
                      className="projectsActionButton"
                      variant="outlined"
                      color="error"
                      onClick={() => handleOpenDeleteDialog()}
                    >
                      Delete Projects
                    </Button>
                  ) : null}
                  <Button
                    className="projectsActionButton"
                    variant="contained"
                    color="secondary"
                    onClick={handleOpenCreateDialog}
                  >
                    Add New Project
                  </Button>
                </Box>
              </Box>
              {!isProjectsWelcomeDismissed ? (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    mb: 2,
                    px: 3,
                    py: 2.5,
                    border: "1px solid #C8D9F3",
                    borderRadius: "12px",
                    backgroundColor: "#F5F9FF",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        mb: 1,
                        fontSize: "20px",
                        fontWeight: 600,
                        color: "primary.main",
                      }}
                    >
                      Welcome to Treegress!
                    </Typography>
                    <Button
                      className="projectsWelcomeTutorialButton"
                      variant="text"
                      color="secondary"
                      onClick={() => setIsProjectsTutorialOpen(true)}
                    >
                      Watch a short video tutorial and learn how to start testing.
                    </Button>
                    <Typography sx={{ mb: 1 }}>
                      Start by adding your project.
                    </Typography>
                    <Typography>
                      Or explore the Demo Project below to see Treegress in
                      action on a live app. Test cases are already generated and
                      you can interact with it or run your own anytime. This
                      gives you a clear picture of the insights you’ll get for
                      your project.
                    </Typography>
                    <Typography sx={{ mt: 1 }}>
                      Find the demo URL and credentials in Pages under the
                      &ldquo;i&rdquo; icon.
                    </Typography>
                  </Box>
                  <IconButton
                    className="projectsWelcomeDismissButton"
                    onClick={handleDismissProjectsWelcome}
                    aria-label="Dismiss welcome block"
                  >
                    <CloseOutlinedIcon />
                  </IconButton>
                </Box>
              ) : null}
              <Box className="projectsMiddleSection">
                <SearchBar list={projectsList} handleSearch={handleSearch} />
              </Box>
              <TableContainer
                className="projectsTableContainer"
                component={Paper}
              >
                <Table className="projectsTable" stickyHeader>
                  <TableHead className="projectsTableHeader">
                    <TableRow>
                      <TableCell className="projectsTableHeaderCheckboxCell">
                        <Checkbox
                          className="projectsTableHeaderCheckbox"
                          checked={isHeaderCheckboxChecked}
                          onChange={(e) => {
                            const allChecked = e.target.checked;
                            if (allChecked) {
                              handleCheckAllCurrentPage();
                            } else {
                              handleUncheckAllCurrentPage();
                            }
                          }}
                        />
                        <IconButton
                          onClick={handleClickCheckAllDropdown}
                          size="small"
                          sx={{ p: 0 }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={openAnchorEl}
                          onClose={handleCloseCheckAllDropdown}
                        >
                          <MenuItem
                            onClick={handleCheckAllCurrentPage}
                            sx={{ minWidth: "100px" }}
                          >
                            All
                          </MenuItem>
                          <MenuItem onClick={handleUncheckAllCurrentPage}>
                            None
                          </MenuItem>
                        </Menu>
                      </TableCell>
                      <TableCell
                        className="projectsTableHeaderCell projectName"
                        onClick={() => handleSortChange("name")}
                      >
                        <Box className="projectsTableHeaderCellContent">
                          <SortingIcon
                            sortConfig={sortConfig}
                            currentKey={"name"}
                          />
                          Project
                        </Box>
                      </TableCell>
                      <TableCell
                        className="projectsTableHeaderCell projectUrl"
                        onClick={() => handleSortChange("url")}
                      >
                        <Box className="projectsTableHeaderCellContent">
                          <SortingIcon
                            sortConfig={sortConfig}
                            currentKey={"url"}
                          />
                          URL
                        </Box>
                      </TableCell>
                      <TableCell
                        className="projectsTableHeaderCell"
                        onClick={() => handleSortChange("owner")}
                      >
                        <Box className="projectsTableHeaderCellContent">
                          <SortingIcon
                            sortConfig={sortConfig}
                            currentKey={"owner"}
                          />
                          Owner
                        </Box>
                      </TableCell>
                      <TableCell
                        className="projectsTableHeaderCell"
                        onClick={() => handleSortChange("created_at")}
                      >
                        <Box className="projectsTableHeaderCellContent">
                          <SortingIcon
                            sortConfig={sortConfig}
                            currentKey={"created_at"}
                          />
                          Last Discovery Date
                        </Box>
                      </TableCell>
                      <TableCell
                        className="projectsTableHeaderCell"
                        onClick={() => handleSortChange("monthly_usage")}
                      >
                        <Box className="projectsTableHeaderCellContent">
                          <SortingIcon
                            sortConfig={sortConfig}
                            currentKey={"monthly_usage"}
                          />
                          Monthly Usage
                        </Box>
                      </TableCell>
                      <TableCell className="projectsTableHeaderCellContent projectActions">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedProjects.map((project) => (
                      <ProjectListItem
                        key={project.id}
                        project={project}
                        checkedProjects={checkedProjects}
                        handleCheckboxChange={handleCheckboxChange}
                        handleOpenDeleteDialog={handleOpenDeleteDialog}
                        handleOpenShareSettings={handleOpenShareSettings}
                        monthlyUsage={usageMap[project.id] || 0}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {paginatedProjects.length ? (
                <PaginationComponent
                  page={page}
                  totalItems={filteredProjects.length}
                  itemsPerPage={projectsPerPage}
                  count={Math.ceil(filteredProjects.length / projectsPerPage)}
                  handlePageChange={handlePageChange}
                />
              ) : null}
            </>
          ) : (
            <Box className="projectsEmpty">
              <Box className="projectsEmptyIconContainer">
                <EmptyProjectsIcon />
              </Box>
              <Typography className="projectsEmptyLabel">
                No Projects
              </Typography>
              <Typography className="projectsEmptyText" variant="body2">
                Start by adding a new project using the &apos;Add New
                Project&apos; button
              </Typography>
              <Button
                className="projectsEmptyAddButton"
                variant="contained"
                color="secondary"
                onClick={handleOpenCreateDialog}
              >
                Add New Project
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      <CreateProjectModal
        open={open}
        setOpen={setOpen}
        projectName={projectName}
        setProjectName={setProjectName}
        projectURL={projectURL}
        setProjectURL={setProjectURL}
        projectsToggler={projectsToggler}
        setProjectsToggler={setProjectsToggler}
        isExistedProjects={Boolean(projectsList.length)}
      />
      <DeleteProjectsConfirmationModal
        deleteOpen={deleteOpen}
        setDeleteOpen={setDeleteOpen}
        projectsToggler={projectsToggler}
        setProjectsToggler={setProjectsToggler}
        projectsToDelete={projectsToDelete}
        setCheckedProjects={setCheckedProjects}
      />
      <YoutubeVideoDialog
        open={isProjectsTutorialOpen}
        onClose={() => setIsProjectsTutorialOpen(false)}
        videoId={PROJECTS_START_TUTORIAL_VIDEO_ID}
        title="How to start testing"
      />
    </Box>
  );
};

export default Projects;
